import { AvatarGroup, Box, Icon, Stack, TableColumns } from '@ff-it/ui';
import { BlockWidget, BoundStates, CampaignWidget, ClientTitle } from 'components';
import { PeriodWidget } from 'components/PeriodWidget';
import { UserAvatar } from 'components/UserAvatar';
import { CampaignStateTag } from 'modules/campaign/status/CampaignStateTag';
import type { ListCampaign } from 'modules/campaign/campaign/types';
import { BlockStatusTag } from 'modules/campaign/status/BlockStatusTag';
import { type ListBlock } from 'modules/campaign/block/types';
import { formatDistanceStrict, parseISO } from 'date-fns';
import { DeadlineDate } from 'components/DeadlineDate';

const startDateColumn = {
  content: 'Start date',
  key: '_days',
  render: ({ date_from }: { date_from: string }) => {
    // DateDistance?
    const now = new Date();
    const dateFrom = parseISO(date_from);

    const distance = formatDistanceStrict(dateFrom, now, { addSuffix: true });
    if (dateFrom < now) {
      return (
        <Box as="span" fontWeight="semibold">
          <Icon icon="fire" mr="1" color="error600" />
          {distance}
        </Box>
      );
    }

    return distance;
  },
} as const;

const blockPerifxColumns: TableColumns<ListBlock> = [
  {
    width: '120px',
    key: 'status',
    content: 'Status',
    render: ({ status }) => <BlockStatusTag status={status} appearance="naked" />,
  },
  {
    width: '180px',
    key: 'id',
    content: 'Block',
    render: (block) => <BlockWidget {...block} campaign_id={block.campaign.id} link />,
  },
  {
    width: '180px',
    key: 'campaign.id',
    content: 'Campaign',
    render: ({ campaign }) => <CampaignWidget {...campaign} link />,
  },
  {
    key: 'campaign.client.id',
    content: 'Client',
    render: ({ campaign }) => <ClientTitle {...campaign} />,
  },
];

const blockSuffixColumns: TableColumns<ListBlock> = [
  {
    key: '_period',
    content: 'Period',
    render: ({ date_from, date_to, deadline }) => (
      <Stack direction="horizontal" textSize="xs">
        {deadline && <DeadlineDate deadline={deadline} />}
        <PeriodWidget date_from={date_from} date_to={date_to} />
      </Stack>
    ),
  },
  {
    width: '100px',
    content: 'Manager',
    key: 'manager',
    cellProps: {
      textAlign: 'center',
    },
    render: ({ campaign: { manager } }) => <UserAvatar user={manager} size="xs" />,
  },
  {
    key: 'planners',
    width: '100px',
    cellProps: {
      textAlign: 'center',
    },
    content: 'Planners',
    render: ({ planners }) => (
      <AvatarGroup size="xs">
        {planners.map((user) => (
          <UserAvatar user={user} key={user.id} />
        ))}
      </AvatarGroup>
    ),
  },
];
export const unassingedBlockColumns: TableColumns<ListBlock> = [
  ...blockPerifxColumns,
  startDateColumn,
  ...blockSuffixColumns,
];

export const planningBlockColumns: TableColumns<ListBlock> = [
  ...blockPerifxColumns,
  {
    content: 'Deadline',
    key: '_days',
    render: ({ deadline }: { deadline: string | null }) => {
      if (!deadline) {
        return null;
      }
      // DateDistance?
      const now = new Date();
      const dateFrom = parseISO(deadline);

      const distance = formatDistanceStrict(dateFrom, now, { addSuffix: true });
      return distance;
    },
  },
  ...blockSuffixColumns,
];

export const pendingBlockColumns: TableColumns<ListBlock> = [
  ...blockPerifxColumns,
  {
    content: 'Starts in',
    key: '_days',
    render: ({ date_from }: { date_from: string }) => {
      const now = new Date();
      const dateFrom = parseISO(date_from);
      const distance = formatDistanceStrict(dateFrom, now, { addSuffix: true });

      return distance;
    },
  },
  ...blockSuffixColumns,
];

export const liveBlockColumns: TableColumns<ListBlock> = [
  ...blockPerifxColumns,
  {
    content: 'Ends in',
    key: '_days',
    render: ({ date_to }: { date_to: string }) => {
      const now = new Date();
      const dateFrom = parseISO(date_to);
      const distance = formatDistanceStrict(dateFrom, now, { addSuffix: true });

      return distance;
    },
  },
  ...blockSuffixColumns,
];
export const endedBlockColumns: TableColumns<ListBlock> = [
  ...blockPerifxColumns,
  {
    content: 'Ended',
    key: '_days',
    render: ({ date_to }: { date_to: string }) => {
      const now = new Date();
      const dateFrom = parseISO(date_to);
      const distance = formatDistanceStrict(dateFrom, now, { addSuffix: true });

      return distance;
    },
  },
  ...blockSuffixColumns,
  {
    width: 60,
    cellProps: {
      className: 'align-middle',
    },
    key: 'sums',
    render: ({ sums }: ListBlock) => (sums ? <BoundStates {...sums} /> : null),
  },
];

// Campaign columns are odd-one-out. Since we render empty campaings on block level
export const campaignColumns: TableColumns<ListCampaign> = [
  {
    width: '120px',
    content: 'State',
    key: 'sums.state',
    render: ({ sums: { state } }) => <CampaignStateTag state={state} appearance="naked" />,
  },
  {
    width: '180px',
    content: 'Campaign',
    key: 'id',
    render: (campaign) => <CampaignWidget {...campaign} link />,
  },
  {
    content: 'Client',
    key: 'client.id',
    render: (campaign) => <ClientTitle {...campaign} />,
  },
  startDateColumn,
  {
    content: 'Period',
    key: 'date_from',
    render: ({ date_from, date_to }) => <PeriodWidget date_from={date_from} date_to={date_to} />,
  },
  {
    width: '100px',
    content: 'Manager',
    key: 'manager',
    cellProps: {
      textAlign: 'center',
    },
    render: ({ manager }) => <UserAvatar user={manager} size="xs" />,
  },
];
